import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import type { PayrollRunInput } from '../types';

const schema = z.object({
  period_start: z.string().min(1, 'Required'),
  period_end: z.string().min(1, 'Required'),
  note: z.string(),
});

type FormValues = z.infer<typeof schema>;

const FIELD_KEYS = ['period_start', 'period_end', 'note'] as const;

interface PayrollRunFormProps {
  submitLabel: string;
  onSubmit: (values: PayrollRunInput) => Promise<unknown>;
  onCancel: () => void;
}

export function PayrollRunForm({ submitLabel, onSubmit, onCancel }: PayrollRunFormProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { period_start: '', period_end: '', note: '' },
  });

  async function handleSubmit(values: FormValues) {
    try {
      await onSubmit(values);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          const key = FIELD_KEYS.find((k) => k === field);
          form.setError(key ?? 'root', { message: messages.join(' ') });
        }
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="max-w-xl space-y-5">
        <FormSection
          variant="card"
          title="Payroll run"
          description="The pay period this run covers."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="period_start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Period start</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="period_end"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Period end</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Note</FormLabel>
                <FormControl>
                  <Textarea rows={2} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FormSection>
        {form.formState.errors.root ? (
          <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
